import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from '~/analytics'
import Header from './Header'
import { cn } from '~/util'
import { useAppStore } from '~/hooks'
import { MenuSelect } from '~/components/MenuSelect'
import { COSMOS_SITES, EROSION_SITES, type CosmosSite, type Dataset } from '~/constants'

import MultiSwitch from '../MultiSwitch'
import MultiSwitchOption from '../MultiSwitch/MultiSwitchOption'

export default function Panel() {
  const { t } = useTranslation()
  const { basemapType, setDataset, dataset, cosmosSite, setCosmosSite } = useAppStore()

  const siteOptions = useMemo(() => {
    const sites = dataset === 'erosion' ? EROSION_SITES : COSMOS_SITES
    return sites.map((site) => ({
      label: t(`panel.sites.${site}`),
      value: site,
    }))
  }, [dataset, t])

  const handleDatasetChange = (nextDataset: Dataset) => {
    setDataset(nextDataset)
    track('dataset_selected', { dataset: nextDataset })
  }

  const handleSiteChange = (site: CosmosSite) => {
    setCosmosSite(site)
    track('study_region_selected', { region: site })
  }

  return (
    <section
      className={cn(
        'pointer-events-auto rounded-xl bg-neutral-white',
        basemapType === 'FLAT' ? 'shadow-lg' : ''
      )}
    >
      <Header />
      <div className="space-y-1.5 border-b border-neutral-midfi-grey/20">
        <div className="flex justify-center">
          <MultiSwitch<Dataset> onChange={handleDatasetChange} selection={dataset}>
            <MultiSwitchOption<Dataset>
              value="cosmos"
              position="left"
              title={t('panel.flooding')}
            />
            <MultiSwitchOption<Dataset>
              value="erosion"
              position="center"
              title={t('panel.erosion')}
            />
            <MultiSwitchOption<Dataset>
              value="waves"
              position="right"
              title={t('panel.calWaves')}
            />
          </MultiSwitch>
        </div>
        {(dataset === 'cosmos' || dataset === 'erosion') && (
          <div className="space-y-1 px-3 pb-3">
            <label className="block text-left text-sm font-medium text-neutral-midfi-grey">
              {t('panel.studyRegion')}
            </label>
            <MenuSelect<CosmosSite>
              onChange={(option) => handleSiteChange(option.value)}
              defaultValue={siteOptions.find(({ value }) => value === cosmosSite)!}
              options={siteOptions}
            />
          </div>
        )}
      </div>
    </section>
  )
}
